import { useState, useCallback, useEffect } from "react";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { ideasService } from "../service/api";
import { useToast } from "@/core/hooks/useToast";
import { OwnerInvitationsResponse } from "../model/types";
import {
  fetchIdeaDetailRequested,
  fetchIdeaInvitationsRequestsRequested,
} from "../store";

export const useOwnerInvitationsViewModel = (
  ideaId: string | undefined,
  isOwner: boolean
) => {
  const [invitations, setInvitations] =
    useState<OwnerInvitationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [withdrawingInvitation, setWithdrawingInvitation] = useState<
    string | null
  >(null);
  const [processingRequest, setProcessingRequest] = useState<string | null>(
    null
  );
  const [emailInputs, setEmailInputs] = useState<string[]>(["", "", ""]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const { showError, showSuccess } = useToast();

  const fetchInvitations = useCallback(async () => {
    if (!ideaId || !isOwner) return;

    setLoading(true);
    try {
      const response = await apiService.get<any>(
        API_ENDPOINTS.IDEAS.GET_INVITATIONS_OWNER(ideaId)
      );
      setInvitations(response.data as OwnerInvitationsResponse);
    } catch (error: any) {
      console.error("Failed to fetch owner invitations:", error);
      showError(error?.error || "Failed to fetch owner invitations");
      setInvitations(null);
    } finally {
      setLoading(false);
    }
  }, [ideaId, isOwner, showError]);

  useEffect(() => {
    if (isOwner) {
      fetchInvitations();
    }
  }, [isOwner, fetchInvitations]);

  const withdrawInvitation = useCallback(
    async (actionId: string, targetUserId: string) => {
      if (!ideaId) return;

      try {
        setWithdrawingInvitation(actionId);
        await ideasService.withdrawJoinIdea(
          ideaId,
          targetUserId,
          "invitation",
          "remove"
        );

        fetchIdeaDetailRequested(ideaId);
        fetchIdeaInvitationsRequestsRequested();
        await fetchInvitations();
      } catch (error: any) {
        console.error("Failed to withdraw invitation:", error);
        showError(error?.error || "Failed to withdraw invitation");
      } finally {
        setWithdrawingInvitation(null);
      }
    },
    [ideaId, fetchInvitations, showError]
  );

  const acceptRequest = useCallback(
    async (actionId: string) => {
      try {
        setProcessingRequest(actionId);
        await ideasService.acceptActionAnswer("accept", actionId);

        if (ideaId) {
          fetchIdeaDetailRequested(ideaId);
          fetchIdeaInvitationsRequestsRequested();
        }
        await fetchInvitations();
      } catch (error: any) {
        console.error("Failed to accept request:", error);
        showError(error?.error || "Failed to accept request");
      } finally {
        setProcessingRequest(null);
      }
    },
    [ideaId, fetchInvitations, showError]
  );

  const denyRequest = useCallback(
    async (actionId: string) => {
      try {
        setProcessingRequest(actionId);
        await ideasService.acceptActionAnswer("deny", actionId);

        if (ideaId) {
          fetchIdeaDetailRequested(ideaId);
          fetchIdeaInvitationsRequestsRequested();
        }
        await fetchInvitations();
      } catch (error: any) {
        console.error("Failed to deny request:", error);
        showError(error?.error || "Failed to deny request");
      } finally {
        setProcessingRequest(null);
      }
    },
    [ideaId, fetchInvitations, showError]
  );

  const addEmailInput = useCallback(() => {
    setEmailInputs((prev) => [...prev, ""]);
  }, []);

  const updateEmailInput = useCallback((index: number, value: string) => {
    setEmailInputs((prev) =>
      prev.map((email, i) => (i === index ? value : email))
    );
  }, []);

  const sendInvitations = useCallback(async () => {
    const emails = emailInputs.filter((email) => email.trim() !== "");
    if (emails.length === 0 || !ideaId) return;

    setInvitationsLoading(true);
    try {
      const result: any = await apiService.post(
        API_ENDPOINTS.IDEAS.SEND_INVITATIONS,
        { emails, idea_id: ideaId }
      );

      if (result?.results && Array.isArray(result.results)) {
        let hasSuccess = false;
        result.results.forEach((emailResult: any) => {
          if (emailResult.status === "success") {
            hasSuccess = true;
            showSuccess(`Invitation sent successfully to ${emailResult.email}`);
          } else if (emailResult.status === "error") {
            showError(
              `Failed to send invitation to ${emailResult.email}: ${emailResult.message}`
            );
          }
        });

        if (hasSuccess) {
          setEmailInputs(["", "", ""]);
        }
      }

      fetchIdeaDetailRequested(ideaId);
      fetchIdeaInvitationsRequestsRequested();
      await fetchInvitations();
    } catch (error: any) {
      console.error("Failed to send invitations:", error);
      showError(error?.error || "Failed to send invitations");
    } finally {
      setInvitationsLoading(false);
    }
  }, [emailInputs, ideaId, fetchInvitations, showError, showSuccess]);

  return {
    invitations,
    loading,
    withdrawingInvitation,
    processingRequest,
    emailInputs,
    invitationsLoading,
    withdrawInvitation,
    acceptRequest,
    denyRequest,
    addEmailInput,
    updateEmailInput,
    sendInvitations,
  };
};
